// ---------------------------------------------------------------------------
// Program application form logic — section pagination + answer-based branching.
// Shared by the editor preview, the applicant form (PublicApply), and the
// in-app apply modal so navigation & required-validation stay consistent.
//
// Branching model (Google-Forms style):
//   - Questions are an ordered list. A `section` item starts a new "page".
//   - A single_choice/dropdown question with `branch_on_answer` maps each option
//     (by index, via `option_targets`) to a destination: a section id, the
//     sentinel 'next' (continue in order), or 'submit' (end the form).
//   - When a page has several branching questions, the LAST answered one wins.
// ---------------------------------------------------------------------------

export const GO_NEXT = 'next';
export const GO_SUBMIT = 'submit';

// Answer shapes: single_choice / dropdown / text questions store a string;
// multi_choice stores an array of the chosen option strings.
export const isMultiChoice = (q) => !!q && q.type === 'multi_choice';

// True when a required question has nothing usable in it.
export function isAnswerEmpty(value) {
  if (value == null) return true;
  if (Array.isArray(value)) return !value.some((v) => (typeof v === 'string' ? v.trim() : v));
  return typeof value === 'string' ? !value.trim() : false;
}

// A single_choice/multi_choice question with `allow_other` shows an extra "Other"
// row with a free-text box. The typed text is stored as the answer verbatim (no
// sentinel), so exports/reviewers read it as-is — any stored value that isn't one
// of the options is therefore an "Other" answer. Branching ignores it (no option
// index → next). The row's label defaults to "Other" and is renameable.
export const OTHER_LABEL_DEFAULT = 'Other';

export function otherLabel(q) {
  return ((q && q.other_label) || '').trim() || OTHER_LABEL_DEFAULT;
}

// The free-text part of an answer, '' when the applicant didn't use "Other".
export function otherValueOf(q, value) {
  if (!q || !q.allow_other) return '';
  const options = q.options || [];
  const values = Array.isArray(value) ? value : [value];
  return values.find((v) => typeof v === 'string' && v !== '' && !options.includes(v)) || '';
}

export function isOtherAnswer(q, value) {
  return otherValueOf(q, value) !== '';
}

// Split the ordered questions into section pages.
// → [{ key, section: <sectionItem|null>, items: [<non-section questions>] }]
// The intro page (before the first section) is only included when it has items.
export function splitIntoPages(questions) {
  const qs = questions || [];
  const pages = [];
  let current = { key: 'intro', section: null, items: [] };
  for (const q of qs) {
    if (q.type === 'section') {
      if (current.items.length || current.section) pages.push(current);
      current = { key: q.id, section: q, items: [] };
    } else {
      current.items.push(q);
    }
  }
  if (current.items.length || current.section) pages.push(current);
  return pages.length ? pages : [{ key: 'intro', section: null, items: [] }];
}

// Given the answers, which destination does `page` route to?
// Returns a page index, or GO_SUBMIT.
export function nextDestination(pages, currentIndex, answers) {
  const page = pages[currentIndex];
  if (page) {
    // Last answered branching question on the page wins.
    for (let i = page.items.length - 1; i >= 0; i--) {
      const q = page.items[i];
      if (!q.branch_on_answer) continue;
      if (q.type !== 'single_choice' && q.type !== 'dropdown') continue;
      const selected = (answers || {})[q.id];
      if (selected == null || selected === '') continue;
      const optIdx = (q.options || []).indexOf(selected);
      if (optIdx < 0) continue;
      const target = (q.option_targets || [])[optIdx];
      if (!target || target === GO_NEXT) break;      // fall through to sequential next
      if (target === GO_SUBMIT) return GO_SUBMIT;
      const dest = pages.findIndex((p) => p.section && p.section.id === target);
      if (dest >= 0) return dest;                     // jump to target section
      break;                                          // target section deleted → sequential
    }
  }
  const seq = currentIndex + 1;
  return seq >= pages.length ? GO_SUBMIT : seq;
}

// Walk the form from the first page following the answers, collecting the ids of
// every question actually reachable on that path. Used for required-validation so
// questions in branched-away sections don't block submission. Loop-guarded.
export function reachableQuestionIds(questions, answers) {
  const pages = splitIntoPages(questions);
  const ids = new Set();
  const seenPages = new Set();
  let idx = 0;
  let guard = 0;
  while (idx >= 0 && idx < pages.length && !seenPages.has(idx) && guard < pages.length + 2) {
    seenPages.add(idx);
    for (const q of pages[idx].items) ids.add(q.id);
    const dest = nextDestination(pages, idx, answers);
    if (dest === GO_SUBMIT) break;
    idx = dest;
    guard += 1;
  }
  return ids;
}
